import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  CheckCircle2,
  Phone,
  PhoneCall,
  CalendarCheck,
  Globe,
  Search,
  MapPin,
  Target,
  Bot,
  Megaphone,
  Star,
  AlertTriangle,
  TrendingUp,
  Heart,
  Shield,
  Zap,
} from "lucide-react";
import { buildIndustryCityLinks } from "@/lib/industryCityLinks";
import { getIndustryLeadsCopy } from "@/lib/industryLeadsCopy";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { LazyMount } from "@/components/LazyMount";
import { MeetChrisVideoButton } from "@/components/MeetChrisVideoButton";
import { PricingTeaserStrip } from "@/components/PricingTeaserStrip";

import {
  RelatedPagesGrid,
  type RelatedPageCard,
} from "@/components/RelatedPagesGrid";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import { LOCAL_BUSINESS_SCHEMA, SCHEMA_LOCALBUSINESS_ID } from "@/lib/entitySchema";
import { getIndustryHubConfig, type ServiceKey } from "@/lib/industryHubConfig";
import { getIndustrySecondaryConfig, SPRINGS_AREAS } from "@/lib/industrySecondaryConfig";
import { resolveResources } from "@/lib/industryResources";
import { IndustryAdviceSection } from "@/components/IndustryAdviceSection";

export type IndustryFAQ = { q: string; a: string };

export type IndustryHubLink = { to: string; label: string; description?: string };

export type IndustryMarketingPageProps = {
  /** Display name, e.g. "HVAC", "Plumbing" */
  industry: string;
  /** Lowercase variant used in body copy, e.g. "hvac", "plumbing" */
  industryLower: string;
  /** URL slug — full path is /[slug] (e.g. "hvac-marketing-colorado") */
  slug: string;
  subheadline: string;
  painPoints: [string, string, string];
  /** Per-service one-line description tailored to this industry. */
  serviceDescriptions: {
    webDesign: string;
    seo: string;
    googleMaps: string;
    ppc: string;
    geoAi: string;
    socialMedia: string;
  };
  testimonial?: { quote: string; role: string; city: string };
  faqs: IndustryFAQ[];
  /** Industry spokes (e.g. /hvac-seo-colorado, /hvac-web-design-colorado). */
  industrySpokes?: IndustryHubLink[];
  /** City hub cross-links — anchor text "[industry] marketing in [city]". */
  cityHubLinks?: IndustryHubLink[];
  /** Optional page-specific sections rendered after the testimonial. */
  extraSections?: import("react").ReactNode;
};

export function IndustryMarketingPage({
  industry,
  industryLower,
  slug,
  subheadline,
  painPoints,
  serviceDescriptions,
  testimonial,
  faqs,
  industrySpokes = [],
  cityHubLinks = [],
  extraSections,
}: IndustryMarketingPageProps) {
  const hub = getIndustryHubConfig(slug);
  const secondary = getIndustrySecondaryConfig(slug);
  const linkFor = (key: ServiceKey, fallback: string): string =>
    hub?.serviceLinks?.[key] ?? fallback;

  const services = [
    { icon: Search, title: "SEO", outcome: "Drives qualified search leads", desc: serviceDescriptions.seo, to: linkFor("seo", "/colorado-springs-seo") },
    { icon: MapPin, title: "Google Maps", outcome: "Captures emergency calls", desc: serviceDescriptions.googleMaps, to: linkFor("googleMaps", "/colorado-springs-google-maps") },
    { icon: Globe, title: "Web Design", outcome: "Converts visitors into calls", desc: serviceDescriptions.webDesign, to: linkFor("webDesign", "/colorado-springs-web-design") },
    { icon: Target, title: "Google Ads / PPC", outcome: "Books jobs this week", desc: serviceDescriptions.ppc, to: linkFor("ppc", "/colorado-springs-ppc") },
    { icon: Bot, title: "GEO AI", outcome: "Wins AI-assistant referrals", desc: serviceDescriptions.geoAi, to: linkFor("geoAi", "/geo-ai") },
    { icon: Megaphone, title: "Social Media", outcome: "Builds local trust + repeat work", desc: serviceDescriptions.socialMedia, to: linkFor("socialMedia", "/digital-marketing") },
  ];

  const stats = [
    { value: "3–5×", label: "More Inbound Leads" },
    { value: "60–120d", label: "To Map-Pack Movement" },
    { value: "24/7", label: "Call Tracking" },
    { value: "97%", label: "Client Retention" },
  ];

  const cityLinks = buildIndustryCityLinks(industry);
  const leadsCopy = getIndustryLeadsCopy(slug, industry, industryLower);

  const relatedServiceLinks = [
    { to: "/seo", label: `${industry} SEO services in Colorado` },
    { to: "/local-seo", label: `Local SEO for ${industryLower} companies` },
    { to: "/google-ads-ppc", label: `${industry} Google Ads management` },
    { to: "/web-design", label: `${industry} website design` },
    { to: "/geo-ai", label: `GEO AI for ${industryLower} businesses` },
    { to: "/google-maps", label: `${industry} Google Maps optimization` },
  ];

  const resourceCards = hub?.resourceSlugs ? resolveResources(hub.resourceSlugs) : [];
  const trustPoints = hub?.trustPoints;


  return (
    <SiteLayout>
      <PageHero
        eyebrow={`${industry} Marketing — Colorado`}
        title={
          <>
            {industry} Marketing & SEO in{" "}
            <span className="text-gradient-gold">Colorado</span>
          </>
        }
        description={subheadline}
        image={hub?.heroImage ? { src: hub.heroImage, alt: hub.heroAlt ?? "" } : undefined}
        hideTrustSignals
      >
        <div className="flex items-center gap-2 text-sm text-brand-cream/90">
          <Heart className="h-4 w-4 text-brand-gold" />
          <span>
            <strong className="font-semibold text-white">Talk To The Owner Directly</strong>
            {" "}— not a salesperson, account manager, or outsourced team.
          </span>
        </div>

        <div className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/contact"
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Get a Free {industry} Marketing Audit
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
          <a
            href="tel:+17194193935"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
           aria-label="Call (719) 419-3935">
            <Phone className="h-4 w-4" /> (719) 419-3935
          </a>
        </div>

        <div className="mt-4 flex justify-center">
          <MeetChrisVideoButton variant="light" />
        </div>

        <div className="mt-6 flex flex-col items-center gap-3 text-sm text-brand-cream/85">
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5 text-brand-gold">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-current" />
              ))}
            </div>
            <span className="font-semibold text-white">150+ 5-Star Reviews across Colorado</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <span className="inline-flex items-center gap-1.5">
              <Shield className="h-4 w-4 text-brand-gold" />
              Colorado-Only Agency
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Zap className="h-4 w-4 text-brand-gold" />
              25+ Years of Experience
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-brand-gold" />
              No Salespeople. No Outsourcing.
            </span>
          </div>
        </div>
      </PageHero>

      {/* Pricing — surfaced directly under the hero for fast qualification */}
      <PricingTeaserStrip />

      {/* Outcomes band — what you actually get */}
      <section className="bg-section-alt py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              What you actually get
            </span>
            <h2 className="mt-3 text-3xl font-extrabold leading-tight text-foreground sm:text-4xl">
              More <span className="text-gradient-gold">leads, calls, and booked jobs</span>{" "}
              for your {industryLower} business
            </h2>
            <p className="mt-3 text-base text-muted-foreground">
              We don't sell impressions or vanity rankings. Every page, ad, and GBP tweak is
              built to put a ringing phone in your hand — and a job on your calendar.
            </p>
          </div>
          <ul className="mt-10 grid gap-5 md:grid-cols-3">
            {[
              {
                icon: TrendingUp,
                title: "More Qualified Leads",
                body: `Ranked pages + an optimized Google Business Profile that capture high-intent searches like "${industryLower} near me" — not tire-kickers.`,
              },
              {
                icon: PhoneCall,
                title: "More Phone Calls",
                body: `Mobile-first, click-to-call ${industryLower} websites with real call tracking so you know exactly which keyword, ad, or page made the phone ring.`,
              },
              {
                icon: CalendarCheck,
                title: "More Booked Jobs",
                body: `Landing pages, trust signals, and follow-up built to turn calls into scheduled work — not missed voicemails.`,
              },
            ].map((o) => (
              <li
                key={o.title}
                className="flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-card"
              >
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-secondary text-brand-gold">
                  <o.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-xl font-bold text-foreground">{o.title}</h3>
                <p className="mt-2 leading-relaxed text-muted-foreground">{o.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>



      {/* Industry image + service keywords / coverage areas */}
      {secondary && (
        <section className="bg-background py-20 lg:py-28">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="grid items-center gap-12 lg:grid-cols-2">
              <div className="overflow-hidden rounded-2xl border border-border shadow-card">
                <img
                  src={secondary.image}
                  alt={secondary.alt}
                  width={1024}
                  height={1024}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                  What we help you rank for
                </span>
                <h2 className="mt-3 text-3xl font-extrabold leading-tight text-foreground sm:text-4xl">
                  {industry} services we help{" "}
                  <span className="text-gradient-gold">Colorado companies</span>{" "}
                  win on Google
                </h2>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                  We build SEO, Google Maps, and Google Ads campaigns around the
                  actual {industryLower} services your customers search for —
                  not vague "marketing." Every page, every keyword, every ad is
                  scoped to a real service line and a real Colorado service area.
                </p>
                <ul className="mt-6 grid gap-2 sm:grid-cols-2">
                  {secondary.services.map((s) => (
                    <li
                      key={s}
                      className="flex items-start gap-2 text-sm font-medium text-foreground"
                    >
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-7">
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-gold-deep">
                    Colorado service areas
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {SPRINGS_AREAS.map((city) => (
                      <span
                        key={city}
                        className="rounded-full border border-border bg-muted/60 px-3 py-1 text-xs font-semibold text-foreground"
                      >
                        {city}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}


      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              The Real Problem
            </span>
            <h2 className="mt-3 text-4xl font-extrabold leading-tight text-foreground sm:text-5xl">
              What {industry} companies in{" "}
              <span className="text-gradient-gold">Colorado struggle with</span>
            </h2>
          </div>
          <ul className="mt-12 grid gap-6 md:grid-cols-3">
            {painPoints.map((p) => (
              <li
                key={p}
                className="flex items-start gap-4 rounded-2xl border border-border bg-card p-6 shadow-card"
              >
                <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-brand-gold-deep" />
                <span className="text-base font-medium leading-relaxed text-foreground">
                  {p}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Services */}
      <section className="bg-section-alt py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              How We Help
            </span>
            <h2 className="mt-5 text-4xl font-extrabold text-foreground sm:text-5xl">
              How we drive {industryLower}{" "}
              <span className="text-gradient-gold">leads, calls & booked jobs</span>
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Six channels, one playbook — each tuned to a specific outcome for{" "}
              {industryLower} owners.
            </p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {services.map((s) => (
              <Link
                key={s.title}
                to={s.to as never}
                className="group rounded-2xl border border-border bg-card p-7 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50 hover:shadow-card-hover"
              >
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-secondary text-brand-gold">
                  <s.icon className="h-6 w-6" />
                </div>
                <div className="mt-5 text-[11px] font-bold uppercase tracking-[0.15em] text-brand-gold-deep">
                  {s.outcome}
                </div>
                <h3 className="mt-1 text-xl font-bold text-foreground">{s.title}</h3>
                <p className="mt-2 leading-relaxed text-muted-foreground">{s.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-gold-deep">
                  Learn more
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>



      {/* Why CWI for [industry] — trust strip */}
      {trustPoints && (
        <section className="bg-background py-14 lg:py-16">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                Why CWI for {industryLower}
              </span>
              <h2 className="mt-3 text-3xl font-extrabold text-foreground sm:text-4xl">
                Built for owners, not <span className="text-gradient-gold">agency middlemen</span>
              </h2>
            </div>
            <ul className="mt-10 grid gap-5 md:grid-cols-3">
              {trustPoints.map((t) => (
                <li
                  key={t}
                  className="flex items-start gap-3 rounded-2xl border border-border bg-card p-6 shadow-card"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold" />
                  <span className="text-base leading-relaxed text-foreground">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Testimonial */}
      {testimonial && <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <div className="rounded-2xl border border-border bg-card p-8 shadow-card sm:p-10">
            <div className="flex gap-1 text-brand-gold">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-current" />
              ))}
            </div>
            <blockquote className="mt-4 text-xl leading-relaxed text-foreground/90 sm:text-2xl">
              "{testimonial.quote}"
            </blockquote>
            <div className="mt-6 text-sm font-semibold text-muted-foreground">
              {testimonial.role} — {testimonial.city}
            </div>
          </div>
        </div>
      </section>}

      {extraSections}

      {/* Results callout */}
      <section className="bg-gradient-dark py-16 text-brand-cream lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold">
              <TrendingUp className="h-4 w-4" /> Real Colorado Results
            </span>
            <h2 className="mt-3 text-3xl font-extrabold text-white sm:text-4xl">
              The numbers behind our{" "}
              <span className="text-gradient-gold">{industryLower}</span> clients
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            {stats.map((s) => (
              <div
                key={s.label}
                className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center backdrop-blur"
              >
                <div className="text-3xl font-black text-brand-gold sm:text-4xl">
                  {s.value}
                </div>
                <div className="mt-1 text-sm font-semibold uppercase tracking-wide text-brand-cream/80">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leads-first CTA — ready to buy / book */}
      <section className="bg-gradient-dark py-16 text-brand-cream lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold">
              <PhoneCall className="h-4 w-4" /> {leadsCopy.eyebrow}
            </span>
            <h2 className="mt-3 text-3xl font-extrabold text-white sm:text-4xl">
              More <span className="text-gradient-gold">{leadsCopy.callType}</span>.{" "}
              More <span className="text-gradient-gold">{leadsCopy.jobType}</span>.
            </h2>
            <p className="mt-3 text-base text-brand-cream/80">
              {leadsCopy.subhead}
            </p>
          </div>

          {/* Industry-specific outcome bullets */}
          <div className="mx-auto mt-8 grid max-w-4xl gap-3 sm:grid-cols-3">
            {leadsCopy.bullets.map((b, i) => {
              const Icon = [TrendingUp, PhoneCall, CalendarCheck][i] ?? CheckCircle2;
              return (
                <div
                  key={b}
                  className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-4 text-left"
                >
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold" />
                  <p className="text-sm font-medium leading-snug text-brand-cream">{b}</p>
                </div>
              );
            })}
          </div>



          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {cityLinks.slice(0, 6).map((c) => (
              <Link
                key={c.to}
                to={c.to as never}
                className="group flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-5 py-3.5 text-sm font-semibold text-brand-cream transition-smooth hover:border-brand-gold/60 hover:bg-white/10 hover:text-white"
              >
                <span className="inline-flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-brand-gold" />
                  {c.label}
                </span>
                <ArrowRight className="h-4 w-4 text-brand-gold transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>

          {cityLinks.length > 6 && (
            <div className="mt-6 text-center">
              <Link
                to="/service-areas"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-gold hover:text-brand-gold-deep"
              >
                See all Colorado markets
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              Get a Free {industry} Marketing Audit
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="tel:+17194193935"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-white/20 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
              aria-label="Call (719) 419-3935"
            >
              <Phone className="h-4 w-4" /> (719) 419-3935
            </a>
          </div>
        </div>
      </section>

      {/* Industry spokes — drill-down service pages for this vertical */}
      <IndustryAdviceSection slug={slug} />
      {industrySpokes.length > 0 && (
        <section className="bg-background py-20 lg:py-28">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
                {industry} Services
              </span>
              <h2 className="mt-5 text-3xl font-extrabold text-foreground sm:text-4xl">
                Dedicated {industryLower} services
              </h2>
              <p className="mt-3 text-base text-muted-foreground">
                Drill down into the specific {industryLower} service you need —
                each is a focused playbook for that channel.
              </p>
            </div>
            <ul
              className={`mt-10 grid gap-5 sm:grid-cols-2 ${
                industrySpokes.length >= 3 ? "lg:grid-cols-3" : "lg:grid-cols-2"
              } ${
                industrySpokes.length === 1
                  ? "mx-auto max-w-md sm:grid-cols-1"
                  : industrySpokes.length === 2
                    ? "mx-auto max-w-4xl"
                    : ""
              }`}
            >
              {industrySpokes.map((spoke) => (
                <li key={spoke.to}>
                  <Link
                    to={spoke.to}
                    className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/60 hover:shadow-card-hover"
                  >
                    <h3 className="text-lg font-extrabold text-foreground group-hover:text-brand-gold-deep">
                      {spoke.label}
                    </h3>
                    {spoke.description && (
                      <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                        {spoke.description}
                      </p>
                    )}
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-brand-gold-deep">
                      Read more
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Cross-link: city hubs (industry × city anchor text) */}
      {cityHubLinks.length > 0 && (
        <LazyMount minHeight={400} rootMargin="600px 0px">
          <RelatedPagesGrid
            heading="More from Colorado Web Impressions"
            intro={`See how we help ${industryLower} companies in specific Colorado cities.`}
            cards={cityHubLinks.map<RelatedPageCard>((c) => ({
              to: c.to,
              title: c.label,
              description:
                c.description ??
                `Owner-led ${industryLower} marketing for businesses in this Colorado market.`,
            }))}
          />
        </LazyMount>
      )}

      {/* Statewide city link grid — keyword-rich anchors to every city hub */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              Statewide coverage
            </span>
            <h2 className="mt-2 text-3xl font-extrabold text-foreground sm:text-4xl">
              Serving {industryLower} companies across{" "}
              <span className="text-gradient-gold">Colorado</span>
            </h2>
            <p className="mt-3 text-base text-muted-foreground">
              Pick your market — every page is a focused playbook for {industryLower}{" "}
              owners in that city.
            </p>
          </div>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {cityLinks.map((c) => (
              <li key={c.to}>
                <Link
                  to={c.to as never}
                  className="group flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
                >
                  <span className="inline-flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-brand-gold" />
                    {c.label}
                  </span>
                  <ArrowRight className="h-4 w-4 text-brand-gold-deep transition-transform group-hover:translate-x-1" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Related services — keyword-rich deep links to service hubs */}
      <section className="bg-section-alt py-14 lg:py-16">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              Related services
            </span>
            <h2 className="mt-2 text-2xl font-extrabold text-foreground sm:text-3xl">
              Every channel that drives {industryLower}{" "}
              <span className="text-gradient-gold">leads & calls</span>
            </h2>
          </div>
          <ul className="mt-6 flex flex-wrap gap-2">
            {relatedServiceLinks.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to as never}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
                >
                  {l.label}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>



      {/* Bundle packages — pricing for the industry playbook */}
      {/* Resources & guides — pulls from /answers and /learn */}
      {resourceCards.length > 0 && (
        <section className="bg-background py-20 lg:py-28">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
                Resources & Guides
              </span>
              <h2 className="mt-5 text-3xl font-extrabold text-foreground sm:text-4xl">
                Free reads for <span className="text-gradient-gold">{industryLower}</span> owners
              </h2>
              <p className="mt-3 text-base text-muted-foreground">
                Honest, no-fluff articles from our team — the same playbooks we use with paying clients.
              </p>
            </div>
            <ul className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {resourceCards.map((c) => (
                <li key={c.to}>
                  <Link
                    to={c.to as never}
                    className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/60 hover:shadow-card-hover"
                  >
                    <h3 className="text-base font-extrabold leading-snug text-foreground group-hover:text-brand-gold-deep">
                      {c.title}
                    </h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                      {c.description}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-brand-gold-deep">
                      Read the guide
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* FAQ */}

      <section className="bg-section-alt py-20 lg:py-28">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-extrabold text-foreground sm:text-5xl">
              {industry} Marketing —{" "}
              <span className="text-gradient-gold">Colorado FAQs</span>
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10 w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`}>
                <AccordionTrigger className="text-left text-base font-semibold text-foreground">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-muted-foreground">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <h2 className="text-4xl font-extrabold text-foreground sm:text-5xl">
            Get a Free{" "}
            <span className="text-gradient-gold">{industry} Marketing Audit</span>
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground">
            Owner-led strategy from our Colorado Springs HQ. No contracts, no
            outsourcing, no pressure — just real recommendations for your{" "}
            {industryLower} business.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              Get My Free {industry} Marketing Audit
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="tel:+17194193935"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-7 py-4 text-base font-semibold text-foreground transition-smooth hover:border-brand-gold/50"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" /> (719) 419-3935
            </a>
          </div>
          <address className="mt-8 not-italic text-sm text-muted-foreground">
            3210 E Woodmen Rd STE 110, Colorado Springs, CO 80920
            {" "}|{" "}
            <a href="tel:+17194193935" className="font-semibold text-foreground hover:text-brand-gold-deep" aria-label="Call (719) 419-3935">
              (719) 419-3935
            </a>
          </address>
        </div>
      </section>
    </SiteLayout>
  );
}

/**
 * Build head() metadata for an industry marketing route.
 *
 * Pass `canonicalOverride` (absolute URL) when this route's content overlaps a
 * flagship hub and you want to consolidate ranking signal onto the flagship —
 * e.g. /roofing-seo-colorado overlaps /roofing-marketing-colorado.
 * Schema @id values still use this page's own URL so each page stays uniquely
 * identifiable in the structured-data graph.
 */
export function industryMarketingHead(
  industry: string,
  industryLower: string,
  slug: string,
  faqs: IndustryFAQ[],
  canonicalOverride?: string,
) {
  const title = `${industry} Marketing & SEO in Colorado | Colorado Web Impressions`;
  const description = `Get more leads, calls & booked jobs for your Colorado ${industryLower} business. Owner-led SEO, Google Maps, web design & PPC from Colorado Springs. (719) 419-3935.`;

  const selfUrl = `https://coloradowebimpressions.com/${slug}`;
  const canonical = canonicalOverride ?? selfUrl;
  const graph = {
    "@context": "https://schema.org",
    "@graph": [
      // Canonical LocalBusiness — emitted on every industry page so search engines
      // and AI crawlers can attribute this Service node to a real local entity.
      LOCAL_BUSINESS_SCHEMA,
      {
        "@type": "Service",
        "@id": `${selfUrl}#service`,
        name: `${industry} Marketing & SEO in Colorado`,
        description,
        provider: { "@id": SCHEMA_LOCALBUSINESS_ID },
        areaServed: { "@type": "State", name: "Colorado" },
        serviceType: `${industry} Marketing`,
        category: `${industry} Marketing`,
        audience: {
          "@type": "BusinessAudience",
          audienceType: `${industry} business owners in Colorado`,
        },
        url: canonical,
        offers: {
          "@type": "AggregateOffer",
          priceCurrency: "USD",
          lowPrice: "1500",
          highPrice: "5000",
          availability: "https://schema.org/InStock",
          seller: { "@id": SCHEMA_LOCALBUSINESS_ID },
        },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${selfUrl}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://coloradowebimpressions.com/" },
          { "@type": "ListItem", position: 2, name: "Industries", item: "https://coloradowebimpressions.com/industries" },
          { "@type": "ListItem", position: 3, name: `${industry} Marketing in Colorado`, item: canonical },
        ],
      },
      {
        "@type": "FAQPage",
        "@id": `${selfUrl}#faq`,
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: canonical },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: canonical }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(graph),
      },
    ],
  };
}
